import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog';

type ShortcutsHostContextValue = {
  openShortcutsHelp: () => void;
  closeShortcutsHelp: () => void;
  toggleShortcutsHelp: () => void;
  isShortcutsHelpOpen: boolean;
};

const ShortcutsHostContext = createContext<ShortcutsHostContextValue | null>(
  null,
);

/**
 * Hosts the keyboard-shortcuts cheatsheet dialog and open API for Layout menu + ⌘/.
 */
export const ShortcutsHostProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);

  const openShortcutsHelp = useCallback(() => setOpen(true), []);
  const closeShortcutsHelp = useCallback(() => setOpen(false), []);
  const toggleShortcutsHelp = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo(
    () => ({
      openShortcutsHelp,
      closeShortcutsHelp,
      toggleShortcutsHelp,
      isShortcutsHelpOpen: open,
    }),
    [open, openShortcutsHelp, closeShortcutsHelp, toggleShortcutsHelp],
  );

  return (
    <ShortcutsHostContext.Provider value={value}>
      {children}
      <KeyboardShortcutsDialog open={open} onClose={closeShortcutsHelp} />
    </ShortcutsHostContext.Provider>
  );
};

export const useShortcutsHost = (): ShortcutsHostContextValue => {
  const ctx = useContext(ShortcutsHostContext);
  if (!ctx) {
    throw new Error('useShortcutsHost must be used within ShortcutsHostProvider');
  }
  return ctx;
};
